/**
 * Venda assinada aguardando o PCP (decisões 8 e 11 do Igor): o comercial
 * assina o contrato e o PCP só digita o pedido na planilha dias depois. Nesse
 * intervalo a obra já tomou lugar na fila da marcenaria, mas a planilha não a
 * enxerga: outra venda poderia ser prometida no mesmo espaço. Por isso cada
 * ambiente da venda `aguardando` entra na fila projetada como camada própria
 * (`venda_assinada`), com a entrega desejada, até o PCP ligá-la ao número do
 * pedido; ligada, ela sai e passam a valer as linhas da planilha.
 *
 * A ligação é sempre do PCP. A página só SUGERE ("o pedido X parece ser esta
 * venda") quando o cliente tem o mesmo nome e a venda da planilha é da época
 * da assinatura; enquanto ninguém confirma, a mesma obra pode contar duas
 * vezes na fila, e a tela diz isso.
 *
 * Contrato do backend: `especificacao-f2-f4.md`, F4.1 (`VENDA`).
 */
import { normalizeText } from "../data";
import { ehIsoValido, somarDiasCorridos } from "./calendario";
import { CAMADA } from "./classificacao";
import { CLASSES, CLASSE } from "./excecoes";

export const ESTADO_VENDA = Object.freeze({
  AGUARDANDO: "aguardando",
  LIGADA: "ligada",
  CANCELADA: "cancelada",
});

/** Mesmos limites do backend (`capacidade/validacao.py`). */
export const MAX_AMBIENTES_DA_VENDA = 60;
export const TAMANHO_CLIENTE = 200;
export const TAMANHO_NOME_AMBIENTE = 120;
export const TAMANHO_OBSERVACAO = 1000;
export const TAMANHO_MOTIVO_CANCELAMENTO = 300;

/** Decisão 11: depois disso sem ligação, a venda vira aviso para o PCP. */
export const DIAS_PARA_AVISO_DE_LIGACAO = 30;

/**
 * Folga da sugestão: a venda da planilha pode ter data um pouco anterior à
 * assinatura registrada aqui (o comercial registra no dia em que lembra).
 */
export const DIAS_DE_TOLERANCIA_DA_SUGESTAO = 7;

/**
 * Nome do cliente para comparar a venda com a planilha: maiúsculas, sem
 * acento e sem pontuação ("João  da Silva." = "JOAO DA SILVA").
 */
export function normalizarCliente(nome) {
  return normalizeText(nome)
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Mesma normalização da chave do ambiente (`chaveAmbiente`). */
export function normalizarPedido(pedido) {
  return normalizeText(pedido);
}

function vendaValida(venda) {
  return (
    venda?.estado === ESTADO_VENDA.AGUARDANDO &&
    Boolean(venda.id) &&
    ehIsoValido(venda.entrega_desejada) &&
    Array.isArray(venda.ambientes) &&
    venda.ambientes.length > 0
  );
}

/** Só as que ainda esperam o PCP: ligada e cancelada não pesam na fila. */
export function vendasAguardando(vendas) {
  return (vendas || []).filter(vendaValida);
}

/**
 * Ambientes das vendas aguardando, no formato de item aberto da fila:
 * camada `venda_assinada`, `entrega` = entrega desejada e a classe (leve,
 * normal, pesado) que pesa na leitura com as exceções. Sem chave nem pedido:
 * não há linha da planilha para abrir nem para reprogramar.
 */
export function itensDeVendasAssinadas(vendas) {
  return vendasAguardando(vendas).flatMap((venda) =>
    venda.ambientes.map((ambiente, i) => ({
      identidade: `venda-assinada:${venda.id}:${i + 1}`,
      chave: null,
      pedido: null,
      cliente: venda.cliente,
      ambiente: ambiente?.nome || `Ambiente ${i + 1}`,
      situacao: "aberto",
      camada: CAMADA.VENDA_ASSINADA,
      entrega: venda.entrega_desejada,
      classe: CLASSES.includes(ambiente?.classe) ? ambiente.classe : CLASSE.NORMAL,
      vendaAssinadaId: venda.id,
      sinais: [],
    })),
  );
}

function maisRecente(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/**
 * Pedidos da planilha, um por número (normalizado): `[{pedido,
 * pedidoNormalizado, cliente, clienteNormalizado, dataVenda, ambientes,
 * linhas}]`. `dataVenda` = a mais recente das linhas; `ambientes` não conta
 * serviço nem cancelado. Linha sem pedido fica de fora (não há o que ligar).
 */
export function pedidosDaPlanilha(linhas) {
  const porPedido = new Map();
  (linhas || []).forEach((linha) => {
    const pedidoNormalizado = normalizarPedido(linha?.pedido);
    if (!pedidoNormalizado) return;
    const grupo = porPedido.get(pedidoNormalizado) || {
      pedido: String(linha.pedido).trim(),
      pedidoNormalizado,
      cliente: linha.cliente || "",
      clienteNormalizado: normalizarCliente(linha.cliente),
      dataVenda: null,
      ambientes: 0,
      linhas: [],
    };
    grupo.linhas.push(linha);
    grupo.dataVenda = maisRecente(grupo.dataVenda, linha.data_venda || null);
    if (!linha.servico && !linha.cancelado) grupo.ambientes += 1;
    porPedido.set(pedidoNormalizado, grupo);
  });
  return [...porPedido.values()];
}

/**
 * A linha da planilha parece ser esta venda? Mesmo cliente (nome
 * normalizado) e venda da planilha a partir de 7 dias antes da assinatura.
 */
function linhaPareceDaVenda(linha, clienteDaVenda, desde) {
  return (
    Boolean(linha.data_venda) &&
    linha.data_venda >= desde &&
    !linha.cancelado &&
    normalizarCliente(linha.cliente) === clienteDaVenda
  );
}

/**
 * Pedidos que parecem ser cada venda aguardando: `{[vendaId]: [pedido]}`
 * (formato de `pedidosDaPlanilha`, só com as linhas que casam), do pedido de
 * venda mais recente para o mais antigo. Venda sem candidato não aparece.
 * Nunca liga: quem confirma é o PCP.
 */
export function sugestoesDeLigacao({ vendas, linhas }) {
  const sugestoes = {};
  vendasAguardando(vendas).forEach((venda) => {
    const cliente = normalizarCliente(venda.cliente);
    if (!cliente || !ehIsoValido(venda.data_assinatura)) return;
    const desde = somarDiasCorridos(venda.data_assinatura, -DIAS_DE_TOLERANCIA_DA_SUGESTAO);
    const candidatas = (linhas || []).filter((linha) => linhaPareceDaVenda(linha, cliente, desde));
    const pedidos = pedidosDaPlanilha(candidatas).sort(
      (a, b) => String(b.dataVenda).localeCompare(String(a.dataVenda)) || a.pedido.localeCompare(b.pedido, "pt-BR", { numeric: true }),
    );
    if (pedidos.length > 0) sugestoes[venda.id] = pedidos;
  });
  return sugestoes;
}

/** Venda aguardando há mais de 30 dias: o PCP precisa ligar (decisão 11). */
export function passouDoAvisoDeLigacao(venda) {
  return Number(venda?.dias_aguardando) > DIAS_PARA_AVISO_DE_LIGACAO;
}

function textoLimpo(valor) {
  return typeof valor === "string" ? valor.replace(/\s+/g, " ").trim() : "";
}

/**
 * Erro do formulário de registro (o mesmo que o backend recusaria), ou
 * `null`. `form` = `{cliente, dataAssinatura, entregaDesejada, ambientes:
 * [{nome, classe}], observacao}`; `hoje` em ISO.
 */
export function erroDoRegistro(form, hoje) {
  const cliente = textoLimpo(form?.cliente);
  if (!cliente) return "Informe o nome do cliente.";
  if (cliente.length > TAMANHO_CLIENTE) return `O nome do cliente passa de ${TAMANHO_CLIENTE} caracteres.`;
  if (!ehIsoValido(form.dataAssinatura)) return "Informe a data em que o contrato foi assinado.";
  if (form.dataAssinatura > hoje) return "A data da assinatura não pode ser no futuro: registre só venda já assinada.";
  if (!ehIsoValido(form.entregaDesejada)) return "Informe a entrega desejada.";
  if (form.entregaDesejada < form.dataAssinatura) return "A entrega não pode ser antes da assinatura.";
  const ambientes = form.ambientes || [];
  if (ambientes.length < 1 || ambientes.length > MAX_AMBIENTES_DA_VENDA) {
    return `Informe de 1 a ${MAX_AMBIENTES_DA_VENDA} ambientes.`;
  }
  if (ambientes.some((a) => textoLimpo(a?.nome).length > TAMANHO_NOME_AMBIENTE)) {
    return `O nome de um ambiente passa de ${TAMANHO_NOME_AMBIENTE} caracteres.`;
  }
  if (ambientes.some((a) => !CLASSES.includes(a?.classe))) return "Escolha leve, normal ou pesado em cada ambiente.";
  if (String(form.observacao || "").trim().length > TAMANHO_OBSERVACAO) {
    return `A observação passa de ${TAMANHO_OBSERVACAO} caracteres.`;
  }
  return null;
}

/** Corpo do `POST /api/capacidade/vendas-assinadas` a partir do formulário. */
export function corpoDoRegistro(form) {
  return {
    cliente: textoLimpo(form.cliente),
    data_assinatura: form.dataAssinatura,
    entrega_desejada: form.entregaDesejada,
    // Nome vazio vai vazio: o backend escreve "Ambiente N", o mesmo texto da fila.
    ambientes: (form.ambientes || []).map((a) => ({ nome: textoLimpo(a.nome), classe: a.classe })),
    observacao: String(form.observacao || "").trim(),
  };
}
